import React, { Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import MainLayout from './layouts/MainLayout'
import AuthGuard from './components/AuthGuard'
import LoadingFallback from './components/LoadingFallback'
import { useAuth0Redux } from './hooks/useAuth0Redux'
import AdminAuthGuard from './components/AdminAuthGuard'
import ScrollToTop from './components/ScrollToTop'
import UnauthorizedModal from './components/UnauthorizedModal'
import WalkdownDetails from './pages/WalkdownDetails'
import ShimmerLoader from './components/common/ShimmerLoader'
import TurnAround from './pages/TurnAround'
import ExternalRedirect from './components/ExternalRedirect'
import TurnAroundDetail from './pages/TurnAroundDetail'
import SiteDocumentation from './pages/SiteDocumentation'
import { QrCodeTemplate } from './pages/QrCode'

// Lazy load page components for code-splitting
const DeviceList = React.lazy(() => import('./pages/DeviceList'))
const DeviceDetails = React.lazy(() => import('./pages/DeviceDetails'));
const GlobalSearchResult = React.lazy(() => import('./pages/GlobalSearchResult'));
const Walkdowns = React.lazy(() => import('./pages/Walkdowns'))
const WalkdownQRCodesPage = React.lazy(() => import('./pages/WalkdownQRCodesPage'))
const Settings = React.lazy(() => import('./pages/Settings'))
const Locations = React.lazy(() => import('./pages/Location'))
const Auth0Callback = React.lazy(() => import('./pages/Auth0Callback'))
const Admin = React.lazy(() => import("./pages/Admin"));
const ValveConditionMonitoring = React.lazy(() => import('./pages/ValveConditionMonitoring'))
const MyTasks = React.lazy(() => import('./pages/MyTasks'))
const DeviceNotFound = React.lazy(() => import('./pages/DeviceNotFound'))

const App: React.FC = () => {
  // Initialize Auth0 and sync with Redux
  useAuth0Redux()

  return (
    <>
      <ScrollToTop />
      <UnauthorizedModal />
      <Routes>
        <Route path="/callback" element={
          <Suspense fallback={<LoadingFallback />}>
            <Auth0Callback />
          </Suspense>
        } />
        <Route path="/devices/qr-code-template/:deviceId" element={
          <Suspense fallback={<LoadingFallback />}>
            <QrCodeTemplate />
          </Suspense>
        } />
        <Route path="/walkdowns/qr-codes/:taskId" element={
          <Suspense fallback={<LoadingFallback />}>
            <WalkdownQRCodesPage />
          </Suspense>
        } />

        <Route path="/" element={
          <AuthGuard>
            <MainLayout />
          </AuthGuard>
        }>
          <Route index element={<Navigate to="/devices" replace />} />
          <Route path="devices" element={
            <Suspense fallback={<LoadingFallback />}>
              <DeviceList/>
            </Suspense>
          } />
          <Route path="/devices/qr-code-template/:deviceId" element={
            <Suspense fallback={<LoadingFallback />}>
              <QrCodeTemplate />
            </Suspense>
          } />
         
          <Route path="devices/:deviceId" element={
            <Suspense fallback={<LoadingFallback />}>
              <DeviceDetails/>
            </Suspense>
          } />
          <Route path="search-results/:serialNumber"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <GlobalSearchResult />
              </Suspense>
            }
          />
          <Route path="walkdowns" element={
            <Suspense fallback={<LoadingFallback />}>
              <Walkdowns/>
            </Suspense>
          } />          
          <Route
            path="/walkdowns/:id"
            element={
              <Suspense fallback={<ShimmerLoader variant="text" lines={2} height="14px" />}>
                <WalkdownDetails />
              </Suspense>
            }
          />
          <Route
            path="turnarounds" element={
            <Suspense fallback={<LoadingFallback />}>
              <TurnAround/>
            </Suspense>
          } />
           <Route path="turnarounds/:id" element={
            <Suspense fallback={<LoadingFallback />}>
              <TurnAroundDetail/>
            </Suspense>
          } />
          <Route path="site-documentation" element={
            <Suspense fallback={<LoadingFallback />}>
              <SiteDocumentation/>
            </Suspense>
          } />
          <Route path="valve-condition-monitoring" element={
            <Suspense fallback={<LoadingFallback />}>
              <ValveConditionMonitoring/>
            </Suspense>
          } />
          <Route path="my-tasks" element={
            <Suspense fallback={<LoadingFallback />}>
              <MyTasks/>
            </Suspense>
          } />
          <Route path="locations" element={
            <Suspense fallback={<LoadingFallback />}>
              <Locations/>
            </Suspense>
          } />
          
        </Route>
        <Route
            path="/admin"
            element={
              <AdminAuthGuard>
                <Suspense fallback={<LoadingFallback />}>
                  <Admin />
                </Suspense>
              </AdminAuthGuard>
            }
        >
        </Route>

        {/* External redirect for corporate */}
        <Route path="/en/corporate" element={<ExternalRedirect url={import.meta.env.VITE_EMERSON_CART_URL+"/corporate"} />} />
        <Route path="/notFound" element={
            <Suspense fallback={<LoadingFallback />}>
              <DeviceNotFound />
            </Suspense>
          } />
        {/* Fallback for unknown routes */}
        <Route path="*" element={<Navigate to="/" replace />} />

      </Routes>
    </>
    
  )
}

export default App
